import { Button, ButtonSize, ButtonStyle } from "components/buttons/button/button";
import { FormSelect } from "components/form/form-select/form-select";
import { SearchTextInput } from "components/form/inputs/text-inputs/search-text-input/search-text-input";
import { RoleType, RoleTypeDisplayNames } from "models/enumerations/users/role-type";
import { JSX, useState } from "react";
import { defaultUserSearchCriteria, useUserSearch } from "utilities/contexts/search/user/user-search-context";
import { EnumUtils } from "utilities/enumerations/enum-utils";
import { NumberUtils } from "utilities/number-utils";
import { useEffect } from "react";
import { t } from "utilities/localization/t";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------
interface UserFilterComponentProps {
    isLoading: boolean;
}
// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------
const CSS_CLASS_NAME = "user-filter-component";
const DEBOUNCE_TIME = 2000;
// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------
const UserFilterComponent: React.FC<UserFilterComponentProps> = ({ isLoading }): JSX.Element => {

    const { initialCriteria: criteria, setCriteria } = useUserSearch();

    const [emailSearch, setEmailSearch] = useState(criteria?.emailSearchText ?? "");

    const [firstNameSearch, setFirstNameSearch] = useState(criteria?.firstNameSearchText ?? "");

    const [lastNameSearch, setLastNameSearch] = useState(criteria?.lastNameSearchText ?? "");

    const [idSearch, setIdSearch] = useState(criteria?.idSearchValue?.toString() ?? "");

    const triggerSearch = () => {
        const valueAsInt = NumberUtils.parseInt(idSearch);
        setCriteria((prev) => ({
            ...prev,
            emailSearchText: emailSearch,
            firstNameSearchText: firstNameSearch,
            lastNameSearchText: lastNameSearch,
            idSearchValue: valueAsInt,
            currentPage: 1,
        }));
    };

    const handleRoleTypeChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const valueAsInt = NumberUtils.parseInt(event.target.value);
        setCriteria((prev) => ({ ...prev, roleType: valueAsInt, currentPage: 1 }));
    };
    
    const onClearFilterClick = () => {
        setFirstNameSearch("");
        setLastNameSearch("");
        setIdSearch("");
        setEmailSearch("");
        setCriteria((prev) => (defaultUserSearchCriteria));
    };

    useEffect(() => {
        if (isLoading) return;
        const timeout = setTimeout(() => {
            triggerSearch();
        }, DEBOUNCE_TIME);

        // Cancel the previous timer whenever any field changes
        return () => clearTimeout(timeout);
    }, [emailSearch, firstNameSearch, lastNameSearch, idSearch]);
    
    return (
        <div className={`${CSS_CLASS_NAME}__sidebar`}>
            <div className={`${CSS_CLASS_NAME}__sidebar__search`}>
                <SearchTextInput
                    dataTestId="user-list-page:search:input"
                    disabled={isLoading}
                    onSearchTextInputChange={(searchTextInputValue) => {
                        setEmailSearch(searchTextInputValue);
                        setCriteria((prev) => ({ ...prev, currentPage: 1 }));
                    }}
                    onSearchTriggered={triggerSearch}
                    id={"userSearch"}
                    placeholder={t("searchByEmail")}
                    searchTextInputValue={emailSearch}
                />
            </div>
            <div className={`${CSS_CLASS_NAME}__sidebar__search`}>
                <SearchTextInput
                    disabled={isLoading}
                    onSearchTextInputChange={(searchTextInputValue) => {
                        setFirstNameSearch(searchTextInputValue);
                        setCriteria((prev) => ({ ...prev, currentPage: 1 }));
                    }}
                    onSearchTriggered={triggerSearch}
                    id={"userFirstNameSearch"}
                    placeholder={t("searchByFirstName")}
                    searchTextInputValue={firstNameSearch}
                />
            </div>
            <div className={`${CSS_CLASS_NAME}__sidebar__search`}>
                <SearchTextInput
                    disabled={isLoading}
                    onSearchTextInputChange={(searchTextInputValue) => {
                        setLastNameSearch(searchTextInputValue);
                        setCriteria((prev) => ({ ...prev, currentPage: 1 }));
                    }}
                    onSearchTriggered={triggerSearch}
                    id={"userLastNameSearch"}
                    placeholder={t("searchByLastName")}
                    searchTextInputValue={lastNameSearch}
                />
            </div>
            <div className={`${CSS_CLASS_NAME}__sidebar__search`}>
                <SearchTextInput
                    disabled={isLoading}
                    onSearchTriggered={triggerSearch}
                    onSearchTextInputChange={(searchTextInputValue) => {
                        setIdSearch(searchTextInputValue);
                        setCriteria((prev) => ({ ...prev, currentPage: 1 }));
                    }}
                    id={"userIdSearch"}
                    placeholder={t("searchById")}
                    searchTextInputValue={idSearch}
                />
            </div>
            <FormSelect
                dataTestId="user-list-page:filter-by-role:select"
                disabled={isLoading}
                ariaLabelledBy={t("filterByRole")}
                formFieldName={t("filterByRole")}
                id="userFilterByRole"
                label={t("filterByRole")}
                onChange={handleRoleTypeChange}
                options={[
                    { value: "", text: "" },
                    ...EnumUtils.numericEnumToSelectOptions(
                        RoleType,
                        RoleTypeDisplayNames
                    ),
                ]}
                value={criteria?.roleType?.toString()}
            />
            <Button
                dataTestId="user-list-page:clear-all-filters:btn"
                disabled={isLoading}
                onClick={onClearFilterClick}
                size={ButtonSize.Small}
                style={ButtonStyle.Quaternary}
                text={t("clearAllFilters")}
            />
        </div>
    );
}

export default UserFilterComponent;